/*
 * Shared session cache for chat history and transcript hydration.
 *
 * Centralizing this state avoids repeated network fetches when the same
 * session is opened from the sidebar, history page, or optimistic chat flow.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import APIService from "../APIService";
import { buildHistorySessions } from "../utils/chatSessions";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [messagesBySession, setMessagesBySession] = useState({});

  const refreshSessions = useCallback(async () => {
    // Normalize the backend payload once so pages can consume a stable shape.
    const payload = await APIService.GetStudentChats();
    const normalizedSessions = buildHistorySessions(payload);
    setSessions(normalizedSessions);
    return normalizedSessions;
  }, []);

  const fetchMessages = useCallback(async (sessionId) => {
    // Transcripts are cached per session id to avoid duplicate GET requests.
    if (sessionId === null || sessionId === undefined || Number(sessionId) <= 0) {
      return [];
    }

    const cacheKey = String(sessionId);
    const cachedMessages = messagesBySession[cacheKey];
    if (cachedMessages) {
      return cachedMessages;
    }

    const fetchedMessages = await APIService.GetChatMessages(sessionId);
    setMessagesBySession((prevState) => ({
      ...prevState,
      [cacheKey]: fetchedMessages,
    }));
    return fetchedMessages;
  }, [messagesBySession]);

  const setMessages = useCallback((sessionId, nextMessages) => {
    // Callers can pass a next-state function so optimistic updates stay simple.
    if (sessionId === null || sessionId === undefined) {
      return;
    }

    const cacheKey = String(sessionId);
    setMessagesBySession((prevState) => {
      const currentMessages = prevState[cacheKey] || [];
      const resolvedMessages =
        typeof nextMessages === "function"
          ? nextMessages(currentMessages)
          : nextMessages;

      return {
        ...prevState,
        [cacheKey]: Array.isArray(resolvedMessages) ? resolvedMessages : [],
      };
    });
  }, []);

  const reconcileSessionId = useCallback(({ temporarySessionId, realSessionId, sessionData }) => {
    // The first successful prompt replaces the optimistic client id with the persisted id.
    if (
      temporarySessionId === null ||
      temporarySessionId === undefined ||
      realSessionId === null ||
      realSessionId === undefined
    ) {
      return;
    }

    const tempKey = String(temporarySessionId);
    const realKey = String(realSessionId);

    setMessagesBySession((prevState) => {
      if (tempKey === realKey) {
        return prevState;
      }

      const tempMessages = prevState[tempKey];
      if (!tempMessages) {
        return prevState;
      }

      const nextState = { ...prevState, [realKey]: tempMessages };
      delete nextState[tempKey];
      return nextState;
    });

    setSelectedSessionId((currentSelectedSessionId) =>
      String(currentSelectedSessionId) === tempKey ? realSessionId : currentSelectedSessionId
    );

    setSessions((prevSessions) => {
      const normalizedSessionData = sessionData
        ? {
            assignmentName: "",
            preview: "",
            ...sessionData,
            sessionId: realSessionId,
          }
        : null;

      const mappedSessions = prevSessions.reduce((accumulator, session) => {
        const sessionKey = String(session.sessionId);

        if (sessionKey === tempKey) {
          if (normalizedSessionData) {
            accumulator.push({
              ...session,
              ...normalizedSessionData,
            });
          } else {
            accumulator.push({
              ...session,
              sessionId: realSessionId,
            });
          }
          return accumulator;
        }

        if (sessionKey === realKey) {
          accumulator.push(
            normalizedSessionData
              ? {
                  ...session,
                  ...normalizedSessionData,
                }
              : session
          );
          return accumulator;
        }

        accumulator.push(session);
        return accumulator;
      }, []);

      const dedupedSessions = mappedSessions.filter((session, index, collection) => (
        collection.findIndex(
          (candidate) => String(candidate.sessionId) === String(session.sessionId)
        ) === index
      ));

      const hasRealSession = dedupedSessions.some(
        (session) => String(session.sessionId) === realKey
      );

      if (!hasRealSession && normalizedSessionData) {
        return [normalizedSessionData, ...dedupedSessions];
      }

      return dedupedSessions;
    });
  }, []);

  const value = useMemo(
    () => ({
      sessions,
      selectedSessionId,
      messagesBySession,
      setSelectedSessionId,
      refreshSessions,
      fetchMessages,
      setMessages,
      reconcileSessionId,
    }),
    [
      fetchMessages,
      messagesBySession,
      reconcileSessionId,
      refreshSessions,
      selectedSessionId,
      sessions,
      setMessages,
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  // All chat-aware pages are expected to live inside `SessionProvider`.
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSessionContext must be used within a SessionProvider");
  }

  return context;
}
